import React from 'react';

import remarkGfm from 'remark-gfm';
import type { StreamdownProps } from 'streamdown';
import { Streamdown } from 'streamdown';
import { makeStyles } from 'tss-react/mui';

import {
  AnchorElement,
  BlockquoteElement,
  BrElement,
  CodeElement,
  H1Element,
  H2Element,
  H3Element,
  H4Element,
  H5Element,
  H6Element,
  ParagraphElement,
  PreElement,
  PreElementDrafting,
  TableCellElement,
  TableElement,
  TableHeadElement,
} from './elements';
import { cleanMarkdown } from './helpers/clean-markdown';
import { transformMarkdownUrl } from './helpers/transform-markdown-url';
import { ALINE_STREAMDOWN_PLUGINS, getAlineRehypePlugins } from './markdown-plugins';
import { counterResetStyles } from '../../shared/components/Citation';

export { useTypewriter } from './hooks/useTypewriter';

const useStyles = makeStyles()(() => ({
  root: {
    '& li.task-list-item': {
      // Prevents bullet from showing for Markdown checklist items.
      listStyle: 'none',
    },
    ...counterResetStyles,
  },
}));

interface AlineMarkdownProps extends Readonly<StreamdownProps> {
  markdownString: string;
  type: 'message' | 'draft-document';
}

export const AlineMarkdown = React.memo(function AlineMarkdown({
  markdownString,
  rehypePlugins,
  remarkPlugins,
  type,
  ...rest
}: AlineMarkdownProps): JSX.Element {
  const { classes } = useStyles();
  const safeRemarkPlugins = remarkPlugins ?? [];
  const safeRehypePlugins = React.useMemo(
    () => getAlineRehypePlugins(rehypePlugins),
    [rehypePlugins]
  );

  return (
    <Streamdown
      className={classes.root}
      components={{
        a: AnchorElement,
        blockquote: BlockquoteElement,
        br: BrElement,
        code: CodeElement,
        h1: H1Element,
        h2: H2Element,
        h3: H3Element,
        h4: H4Element,
        h5: H5Element,
        h6: H6Element,
        p: ParagraphElement,
        pre: type === 'draft-document' ? PreElementDrafting : PreElement,
        table: TableElement,
        td: TableCellElement,
        th: TableHeadElement,
      }}
      plugins={ALINE_STREAMDOWN_PLUGINS}
      rehypePlugins={safeRehypePlugins}
      remarkPlugins={[...safeRemarkPlugins, remarkGfm]}
      urlTransform={transformMarkdownUrl}
      {...rest}
    >
      {cleanMarkdown(markdownString)}
    </Streamdown>
  );
});
